import moment from "moment-timezone";
import { useEffect, useState } from "react";
import MembershipModel from "src/models/MembershipModel";
import MembershipOfferingModel from "src/models/MembershipOfferingModel";
import UserModel from "src/models/UserModel";
import { useSession } from "src/stores/Session";
import message from "src/utils/message";
import { modals, useModal } from "src/utils/modals";
import tw from "src/utils/tw";
import { CapacityStatus, Level, MembershipStatus } from "types/code-generator";
import { v4 as uuid } from "uuid";
import { BorderButton } from "../buttons/BorderButton";
import InputCheckbox from "../inputs/InputCheckbox";
import InputError from "../inputs/InputError";
import { InputText } from "../inputs/InputText";
import Modal from "./Modal";

interface Props {
  offering: MembershipOfferingModel;
  membership?: MembershipModel;
}

export const AddStudentToMembershipModal = modals.create((props: Props) => {
  const newMembership =
    props.membership ||
    new MembershipModel({
      offering: props.offering,
      status: props.offering.autoEnroll
        ? MembershipStatus.Active
        : MembershipStatus.Pending,
    });

  const session = useSession();
  const [students, setStudents] = useState(session.students || []);
  const [membership, setMembership] = useState(newMembership);
  const [errors, setErrors] = useState<any>({});
  const modal = useModal();

  useEffect(() => {
    if (students.length === 0) {
      addNewStudent();
    }
  }, []);

  useEffect(() => {
    if (Object.keys(errors).length) {
      setErrors({});
    }
  }, [membership]);

  function handleCancel() {
    modal.hide();
  }

  async function validateCurrentStep() {
    const errors = {};

    if (membership.offering.maxMembers) {
      const offeringMax = membership.offering.maxMembers;
      const valid = membership.roster.length <= offeringMax;

      if (!valid) {
        errors["roster"] =
          `This membership must include no more than ${offeringMax} participants per registration.`;
      }
    }

    if (membership.roster.length === 0) {
      errors["roster"] = "Please select at least one participant";
    }

    // if (!membership.offering.allowSharing && membership.roster.length > 1) {
    //   errors["roster"] = "This membership does not allow multiple participants";
    // }

    // for (const sir of membership.offering.studentInfoRequests) {
    // const hasAnswer = membership.studentInfoRequestResponses.find(
    //   (sirr) => sirr.studentInfoRequest.id === sir.id && sirr.description
    // );

    // if (sir.required && !hasAnswer) {
    //   errors[sir.id] = "Please enter a response";
    // }
    // }

    return errors;
  }

  function addNewStudent() {
    const newStudent = new UserModel({
      id: uuid(),
      firstName: `Participant ${students.length + 1}`,
      level: Level.Student,
    });

    const newMembership = new MembershipModel({
      ...membership,
    });

    newMembership.roster.push(newStudent);

    const newStudents = [...students, newStudent];

    setMembership(newMembership);
    setStudents(newStudents);
  }

  function toggleStudentInSeries(checked, student) {
    // console.log("checked", checked, student);

    const newMembership = new MembershipModel({
      ...membership,
    });

    if (checked) {
      newMembership.roster.push(student);
    } else {
      const newRoster = newMembership.roster.filter((s) => s.id !== student.id);
      newMembership.roster = newRoster;
    }

    setMembership(newMembership);
  }

  function setStudentName(firstName, student) {
    const newStudents = [...students];
    const newStudentIndex = newStudents.findIndex((s) => s.id === student.id);

    if (newStudentIndex >= 0) {
      newStudents[newStudentIndex].firstName = firstName;
      setStudents(newStudents);
    }
  }

  function renderStudents() {
    const checkboxes = [];

    for (const s of students) {
      const isInMembership = membership.roster.find((rs) => rs.id === s.id);
      const initialValue = s.firstName;
      const isExisting = !s.id.includes("-");

      checkboxes.push(
        <div key={s.id}>
          <div className={tw("flex", isExisting && "items-center")}>
            <InputCheckbox
              checked={Boolean(isInMembership)}
              onChange={(checked) => toggleStudentInSeries(checked, s)}
            />
            <div className="flex-1">
              <div className="step-subhead relative z-10 -mb-1 pl-4 text-2xs">
                First Name
              </div>
              {!isExisting && (
                <InputText
                  className="border-none bg-white"
                  autoFocus
                  placeholder={`Add New Participant`}
                  value={initialValue}
                  onChange={(checked) => setStudentName(checked, s)}
                  selectTextOnFocus={Boolean(s.firstName)}
                />
              )}
              {isExisting && <div className="px-4">{s.firstName}</div>}
            </div>
          </div>
          <hr />
        </div>
      );
    }

    const body = (
      <div className="mb-6">
        <p>
          Select or add {membership.offering?.participantType.toLowerCase()}s to
          this {membership.offering.type.toLowerCase()}.
        </p>
        <hr />
        <div>{checkboxes}</div>
        <div className="mb-2">
          <InputError error={errors.roster} />
        </div>
        <BorderButton dashed className="w-full" onClick={addNewStudent}>
          <i aria-hidden className="far fa-plus text-organization" /> Add
          another participant
        </BorderButton>
      </div>
    );

    const footer = (
      <>
        <BorderButton
          type="submit"
          onClick={addToCart}
          disabled={Object.keys(errors).length > 0}
        >
          Add to Cart
        </BorderButton>
        <BorderButton onClick={handleCancel}>Cancel</BorderButton>
      </>
    );

    return { body, footer };
  }

  async function addToCart() {
    const errors = await validateCurrentStep();

    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    const processingMessage = message.loading("Adding…");

    if (membership.roster.length === 0) {
      if (membership.id) {
        session.removeRegistration(membership.id);
      }

      handleCancel();
      return;
    }

    if (membership.offering.capacity === CapacityStatus.Waitlist) {
      membership.status = MembershipStatus.Waitlist;
    } else if (membership.offering.autoEnroll) {
      membership.status = MembershipStatus.Active;
      membership.dateApproved = new Date();

      let expiration;

      if (membership.offering.endDays) {
        expiration = moment()
          .endOf("day")
          .add(membership.offering.endDays, "days");
      }

      if (membership.offering.endDate) {
        expiration = moment(membership.offering.endDate);
      }

      if (expiration) {
        membership.finalDate = expiration.toDate();
      }
    }

    if (!membership.offering.allowSharing && membership.roster.length > 1) {
      for (const r of membership.roster) {
        const newMembership = new MembershipModel({
          ...membership,
          roster: [r],
        });

        session.addMembership(newMembership);
      }
    } else {
      session.addMembership(membership);
    }

    const studentsWithNames = students.filter(
      (s) =>
        s.firstName !== "" && membership.roster.find((rs) => rs.id === s.id)
    );

    session.setStudents((current) => {
      const newStudents = [...current];

      for (const s of studentsWithNames) {
        if (!newStudents.find((ns) => ns.id === s.id)) {
          newStudents.push(s);
        }
      }

      return newStudents;
    });

    processingMessage();
    handleCancel();
  }

  const content = renderStudents();

  return (
    <Modal
      visible={modal.visible}
      afterClose={modal.remove}
      handleCancel={handleCancel}
      footer={content.footer}
      title={props.offering.program?.title}
    >
      {content.body}
    </Modal>
  );
});
